import { router } from "@inertiajs/react"
import { useCallback } from "react"

import { missionsPath } from "@/routes"
import type {
  MissionListDeadlineWindow,
  MissionListFilters,
  MissionListMissionType,
  MissionListSort,
  MissionListSortField,
  MissionListStatus,
} from "@/types/missions"

/**
 * OKS-31 — URL state mutator for the Brand Mission Index page.
 *
 * Mirrors `useCrewListUrl` (OKS-25) and `useCandidateListUrl` (OKS-17)
 * shape with three deltas:
 *
 *   1. Filter set: status / mission_type / deadline_window / q
 *      (no location / followers / tags / joined-date — that's Crew).
 *   2. Defaults are DROPPED from the URL — `status=active`,
 *      `mission_type=all`, `deadline_window=all`, sort
 *      `deadline asc` are all the BE defaults (`Missions::ListQuery`
 *      re-applies them when the URL omits the param). Keeps the
 *      back-button URL clean and shareable.
 *   3. Inertia partial-reload `only:` set is locked with the BE
 *      controller's prop bag (`MissionsController#index`):
 *      `["missions", "filters", "sort", "pagination",
 *        "filter_options", "is_empty"]`.
 *
 * Wire-format (FE↔BE locked, contract spec asserts):
 *   - URL keys: `status`, `mission_type`, `deadline_window`, `q`,
 *     `sort`, `dir`, `page`. Defaults omitted; arrays not used.
 *   - Setting any filter resets `page` to 1 (lest the user end up
 *     paginated past the new result count).
 */
const RELOAD_KEYS = [
  "missions",
  "filters",
  "sort",
  "pagination",
  "filter_options",
  "is_empty",
] as const

const DEFAULT_STATUS: MissionListStatus = "active"
const DEFAULT_MISSION_TYPE: MissionListMissionType = "all"
const DEFAULT_DEADLINE_WINDOW: MissionListDeadlineWindow = "all"
const DEFAULT_SORT_FIELD: MissionListSortField = "deadline"
const DEFAULT_SORT_DIR = "asc"

/**
 * Build the URL params object from the next filter/sort/page state.
 * Drops nullish + empty-string + default values so the URL stays
 * minimal. Exported for direct unit testing — the URL round-trip is
 * the canonical canary against drift between FE and BE.
 */
export function buildMissionListParams(
  filters: Partial<MissionListFilters>,
  sort: Partial<MissionListSort>,
  page?: number,
): Record<string, string | number> {
  const params: Record<string, string | number> = {}

  if (filters.status && filters.status !== DEFAULT_STATUS) {
    params.status = filters.status
  }
  if (filters.mission_type && filters.mission_type !== DEFAULT_MISSION_TYPE) {
    params.mission_type = filters.mission_type
  }
  if (
    filters.deadline_window &&
    filters.deadline_window !== DEFAULT_DEADLINE_WINDOW
  ) {
    params.deadline_window = filters.deadline_window
  }
  if (filters.q && filters.q.trim() !== "") {
    params.q = filters.q.trim()
  }
  if (sort.field && sort.field !== DEFAULT_SORT_FIELD) {
    params.sort = sort.field
  }
  // `dir` is dropped only when it matches the FIELD's default direction
  // (deadline → asc; created_at → desc; title → asc). The BE re-applies
  // the per-field default when `dir` is absent.
  const fieldDefault = sort.field
    ? defaultDirForField(sort.field)
    : DEFAULT_SORT_DIR
  if (sort.dir && sort.dir !== fieldDefault) {
    params.dir = sort.dir
  }
  if (typeof page === "number" && page > 1) {
    params.page = page
  }

  return params
}

function defaultDirForField(field: MissionListSortField): "asc" | "desc" {
  switch (field) {
    case "deadline":
      return "asc"
    case "created_at":
      return "desc"
    case "title":
      return "asc"
  }
}

interface UseMissionListUrlReturn {
  /** Push a new filter/sort/page state to the URL via Inertia partial reload. */
  push: (
    nextFilters: Partial<MissionListFilters>,
    nextSort?: Partial<MissionListSort>,
    nextPage?: number,
  ) => void
  /** Set a single filter (keeps others + sort, resets page to 1). */
  setFilter: <K extends keyof MissionListFilters>(
    key: K,
    value: MissionListFilters[K],
  ) => void
  /** Set sort (resets page to 1). */
  setSort: (sort: MissionListSort) => void
  /** Navigate to a different page, preserving filters + sort. */
  setPage: (page: number) => void
  /** Reset everything to defaults (clears all filters + sort + page). */
  clearAll: () => void
}

/**
 * Hook exposing typed setters that emit normalized URL params via
 * Inertia partial reload. Caller passes the current snapshot
 * (filters/sort) so deltas compute without re-reading `usePage()` on
 * every render — same idiom as `useCrewListUrl`.
 */
export function useMissionListUrl(
  filters: MissionListFilters,
  sort: MissionListSort,
): UseMissionListUrlReturn {
  const push = useCallback(
    (
      nextFilters: Partial<MissionListFilters>,
      nextSort?: Partial<MissionListSort>,
      nextPage?: number,
    ) => {
      const merged: MissionListFilters = { ...filters, ...nextFilters }
      const mergedSort: MissionListSort = { ...sort, ...(nextSort ?? {}) }
      const params = buildMissionListParams(merged, mergedSort, nextPage)

      router.get(missionsPath(), params, {
        preserveState: true,
        preserveScroll: true,
        only: [...RELOAD_KEYS],
      })
    },
    [filters, sort],
  )

  const setFilter = useCallback(
    <K extends keyof MissionListFilters>(
      key: K,
      value: MissionListFilters[K],
    ) => {
      push({ [key]: value } as Partial<MissionListFilters>, undefined, 1)
    },
    [push],
  )

  const setSort = useCallback(
    (nextSort: MissionListSort) => {
      push({}, nextSort, 1)
    },
    [push],
  )

  const setPage = useCallback(
    (page: number) => {
      push({}, undefined, page)
    },
    [push],
  )

  const clearAll = useCallback(() => {
    router.get(
      missionsPath(),
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: [...RELOAD_KEYS],
      },
    )
  }, [])

  return { push, setFilter, setSort, setPage, clearAll }
}
